/**
 * localResolve.js — deterministic part-name resolver over the loaded manifest.
 *
 * Mirrors the /resolve rules in knowledge_packs/CONTRACT.md §3 closely enough
 * to be a fallback: normalises left/right/L/R/port/starboard and simple
 * plurals, ranks by token overlap, then by level (S before U before A).
 *
 * Used (a) by fixture mode for phrases without a canned response and (b) by
 * drill-down commands when the knowledge service is unavailable, so "open the
 * right engine" still works offline. Pure: no React, no three.js, no fetch.
 */

const SIDE = {
  left: 'l', port: 'l', l: 'l', lh: 'l',
  right: 'r', starboard: 'r', stbd: 'r', r: 'r', rh: 'r',
};

const STOP = new Set([
  'the', 'a', 'an', 'of', 'to', 'me', 'my', 'please', 'open', 'show', 'go',
  'isolate', 'on', 'in', 'at', 'and', 'for', 'this', 'that', 'part', 'up',
]);

const LEVEL_RANK = { S: 0, U: 1, A: 2 };

function singular(tok) {
  if (tok.length > 3 && tok.endsWith('ies')) return `${tok.slice(0, -3)}y`;
  if (tok.length > 3 && tok.endsWith('s') && !tok.endsWith('ss')) return tok.slice(0, -1);
  return tok;
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Apply an asset's synonym phrases ({ 'check valve': 'non return valve' }) to
 * already lower-cased, space-separated text in one pass: longest phrase first,
 * whole words, so a replacement is never rewritten again by a shorter phrase.
 */
function applyPhrases(text, phrases) {
  if (!phrases || !text) return text;
  const map = new Map();
  for (const [key, to] of Object.entries(phrases)) {
    const from = key.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (from && typeof to === 'string') map.set(from, to.toLowerCase());
  }
  if (map.size === 0) return text;
  const alternation = [...map.keys()].sort((a, b) => b.length - a.length).map(escapeRe).join('|');
  return text.replace(new RegExp(`(^| )(${alternation})(?= |$)`, 'g'), (_, lead, hit) => `${lead}${map.get(hit)}`);
}

/**
 * "Open the RIGHT engines" -> ['r', 'engine']
 * `phrases` (optional, config/assets.js resolverPhrases) rewrite synonyms first,
 * so "check valve" becomes "non return valve".
 */
export function normaliseTokens(text, phrases = null) {
  const flat = String(text ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  return applyPhrases(flat, phrases)
    .split(' ')
    .filter(Boolean)
    .map((t) => SIDE[t] ?? singular(t))
    .filter((t) => !STOP.has(t));
}

/**
 * @param {string} text
 * @param {{byId: Map<string, {id:string, name:string}>}} tree
 * @param {number} [limit]
 * @param {{phrases?: Object<string,string>}} [options]  asset synonym phrases
 * @returns {{cad_id:string, name:string, score:number}[]}
 */
export function resolveLocally(text, tree, limit = 5, { phrases = null } = {}) {
  const q = normaliseTokens(text, phrases);
  if (q.length === 0 || !tree?.byId) return [];
  const qSet = new Set(q);
  const qSide = q.find((t) => t === 'l' || t === 'r') ?? null;

  const scored = [];
  for (const node of tree.byId.values()) {
    const n = normaliseTokens(node.name || node.id);
    if (n.length === 0) continue;
    const nSide = n.find((t) => t === 'l' || t === 'r') ?? null;
    // An explicit side in the question rules out the opposite side outright.
    if (qSide && nSide && qSide !== nSide) continue;
    const nSet = new Set(n);
    let hit = 0;
    for (const t of qSet) if (nSet.has(t)) hit += 1;
    // A side token alone is not a match.
    const meaningful = [...qSet].some((t) => t !== 'l' && t !== 'r' && nSet.has(t));
    if (!meaningful) continue;
    const score = 0.7 * (hit / qSet.size) + 0.3 * (hit / nSet.size);
    scored.push({
      cad_id: node.id,
      name: node.name || node.id,
      score: Math.round(score * 100) / 100,
      rank: LEVEL_RANK[node.id[0]] ?? 3,
    });
  }
  scored.sort((a, b) => (b.score - a.score) || (a.rank - b.rank) || a.cad_id.localeCompare(b.cad_id));
  return scored.slice(0, limit).map(({ cad_id, name, score }) => ({ cad_id, name, score }));
}

export default resolveLocally;
